import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { CheckCircle2, FilePenLine, ListChecks, ReceiptText } from "lucide-react";
import { getErrorMessage } from "../../api/client";
import * as statoLavoriApi from "../../api/statoLavori";
import type { ModuloPromemoria, PromemoriaOrdine } from "../../api/statoLavori";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { SECTION_COLORS, SectionKey } from "../../utils/sectionColors";
import { ROTTA_ATTIVITA, TITOLO_BOZZA, TONO_STATO, dataIt, istanteIt, linkPassoDaFare, nomeCassaforte } from "../StatoLavori/presentazione";

type Tipo = PromemoriaOrdine["tipo"];

// Dove ciascun ordine può mancare: l'OdV non si chiede sulle attivazioni
// (decisione del 12/10/2026), l'OdA su tutte e quattro. Le tessere a zero
// restano, così si vede anche dove non manca niente.
const TESSERE: { tipo: Tipo; titolo: string; moduli: ModuloPromemoria[] }[] = [
  { tipo: "odv", titolo: "OdV mancanti", moduli: ["sopralluogo", "installazione", "intervento"] },
  { tipo: "oda", titolo: "OdA mancanti", moduli: ["sopralluogo", "installazione", "attivazione", "intervento"] },
];

const MODULO: Record<ModuloPromemoria, { etichetta: string; plurale: string; sezione: SectionKey }> = {
  sopralluogo: { etichetta: "Sopralluogo", plurale: "Sopralluoghi", sezione: "sopralluoghi" },
  installazione: { etichetta: "Installazione", plurale: "Installazioni", sezione: "installazioni" },
  attivazione: { etichetta: "Attivazione", plurale: "Attivazioni", sezione: "attivazioni" },
  intervento: { etichetta: "Intervento", plurale: "Interventi", sezione: "interventi" },
};

/** Quante voci si vedono prima di «Mostra tutte». */
const VOCI_INIZIALI = 8;

interface Filtro {
  tipo: Tipo;
  modulo: ModuloPromemoria;
}

/**
 * Riquadro «Da fare» (issue #211): gli OdV e gli OdA mancanti sulle attività
 * concluse dal 1/10/2026, anche fuori pratica, le bozze da confermare delle
 * pratiche aperte (PR C2) e i passi da fare o da verificare (PR D). Solo admin
 * e staff (`stato_lavori:read`): lo monta la dashboard solo per loro. Come
 * «Ritardi», se non manca niente lo dice invece di sparire. I gruppi si
 * caricano ciascuno per conto suo: un errore dell'uno non nasconde gli altri.
 */
export function DaFareCard() {
  const [dati, setDati] = useState<statoLavoriApi.PromemoriaOrdini | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filtro, setFiltro] = useState<Filtro | null>(null);
  const [tutte, setTutte] = useState(false);

  const carica = useCallback(() => {
    setIsLoading(true);
    setErrore(null);
    statoLavoriApi
      .getPromemoriaOrdini()
      .then(setDati)
      .catch((err) => setErrore(getErrorMessage(err, "Impossibile caricare gli ordini mancanti.")))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(carica, [carica]);

  return (
    <section className="mb-8" aria-labelledby="da-fare-titolo">
      <h2 id="da-fare-titolo" className="mb-3 text-sm font-bold uppercase tracking-wide text-ink/50">
        Da fare
      </h2>
      {isLoading && !dati ? (
        <div className="h-24 animate-pulse rounded-card border border-line bg-white/60" />
      ) : errore ? (
        <div className="flex flex-wrap items-center gap-3 rounded-card border border-line bg-white p-4 text-sm text-ink/70 shadow-card" role="alert">
          <span className="min-w-0 flex-1">{errore}</span>
          <Button variant="secondary" size="sm" onClick={carica}>
            Riprova
          </Button>
        </div>
      ) : dati ? (
        <OrdiniMancanti dati={dati} filtro={filtro} setFiltro={setFiltro} tutte={tutte} setTutte={setTutte} />
      ) : null}
      <BozzeDaConfermare />
      <PassiDaFare />
    </section>
  );
}

function OrdiniMancanti({
  dati,
  filtro,
  setFiltro,
  tutte,
  setTutte,
}: {
  dati: statoLavoriApi.PromemoriaOrdini;
  filtro: Filtro | null;
  setFiltro: (f: Filtro | null) => void;
  tutte: boolean;
  setTutte: (v: boolean) => void;
}) {
  const dal = dataIt(dati.data_inizio);
  const totale = dati.promemoria.length;

  if (totale === 0) {
    return (
      <div className="flex items-center gap-2 rounded-card border border-line bg-white p-4 text-sm text-ink/60 shadow-card">
        <CheckCircle2 className="h-5 w-5 shrink-0 text-[#16a34a]" aria-hidden="true" />
        Nessun ordine mancante: le attività concluse dal {dal} hanno OdV e OdA dove servono.
      </div>
    );
  }

  const conta = (tipo: Tipo, modulo: ModuloPromemoria) =>
    dati.promemoria.filter((p) => p.tipo === tipo && p.modulo === modulo).length;
  const voci = filtro ? dati.promemoria.filter((p) => p.tipo === filtro.tipo && p.modulo === filtro.modulo) : dati.promemoria;
  const visibili = tutte ? voci : voci.slice(0, VOCI_INIZIALI);

  return (
    <div className="rounded-card border border-line border-l-4 border-l-[#f97316] bg-white p-4 shadow-card">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <ReceiptText className="h-5 w-5 text-[#c2410c]" aria-hidden="true" />
        <span className="text-2xl font-bold text-[#c2410c]">{totale}</span>
        <span className="text-sm text-ink/60">{totale === 1 ? "ordine mancante" : "ordini mancanti"}</span>
        <span className="basis-full text-xs text-ink/50 sm:basis-auto">
          <span className="hidden sm:inline">· </span>attività concluse dal {dal}
        </span>
      </div>

      <div className="space-y-3">
        {TESSERE.map(({ tipo, titolo, moduli }) => (
          <div key={tipo}>
            <p className="mb-1.5 text-xs font-semibold text-ink/60">{titolo}</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {moduli.map((modulo) => {
                const n = conta(tipo, modulo);
                const attiva = filtro?.tipo === tipo && filtro.modulo === modulo;
                return (
                  <button
                    key={modulo}
                    type="button"
                    disabled={n === 0}
                    aria-pressed={attiva}
                    onClick={() => {
                      setFiltro(attiva ? null : { tipo, modulo });
                      setTutte(false);
                    }}
                    className={clsx(
                      "flex flex-col rounded-lg border px-3 py-2 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary",
                      attiva
                        ? "border-[#f97316] bg-[#f97316]/15"
                        : n > 0
                          ? "border-[#f97316]/30 bg-[#f97316]/5 hover:bg-[#f97316]/10"
                          : "cursor-default border-line",
                    )}
                  >
                    <span className={clsx("text-xl font-bold", n > 0 ? "text-[#c2410c]" : "text-ink/40")}>{n}</span>
                    <span className="text-xs text-ink/60">{MODULO[modulo].plurale}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-baseline justify-between gap-2 border-t border-line pt-3">
        <p className="text-xs font-semibold text-ink/60">
          {filtro
            ? `${filtro.tipo === "odv" ? "OdV" : "OdA"} mancanti — ${MODULO[filtro.modulo].plurale.toLowerCase()} (${voci.length})`
            : "Dalla più vecchia"}
        </p>
        {filtro && (
          <button type="button" className="text-xs font-semibold text-primary hover:underline" onClick={() => setFiltro(null)}>
            Mostra tutti i tipi
          </button>
        )}
      </div>
      <ul className="mt-2 divide-y divide-line">
        {visibili.map((p) => (
          <VoceOrdine key={`${p.tipo}:${p.modulo}:${p.attivita_id}`} p={p} />
        ))}
      </ul>
      {voci.length > VOCI_INIZIALI && (
        <button type="button" className="mt-2 text-xs font-semibold text-primary hover:underline" onClick={() => setTutte(!tutte)}>
          {tutte ? "Mostra meno" : `Mostra tutte (${voci.length})`}
        </button>
      )}
      <p className="mt-3 text-xs text-ink/50">L'OdA non è obbligatorio: il promemoria è solo un'indicazione.</p>
    </div>
  );
}

function VoceOrdine({ p }: { p: PromemoriaOrdine }) {
  const m = MODULO[p.modulo];
  const sede = p.sede ? [p.sede.nome, p.sede.citta].filter(Boolean).join(" — ") || "Sede senza nome" : null;
  return (
    <li className="flex flex-col gap-1 py-2 sm:flex-row sm:items-center sm:gap-3">
      <Link
        to={`${ROTTA_ATTIVITA[p.modulo]}?open=${p.attivita_id}`}
        title={p.testo}
        className="flex min-w-0 items-center gap-2 text-sm font-semibold text-ink hover:underline sm:w-72 sm:shrink-0"
      >
        <Badge color={p.tipo === "odv" ? "warning" : "primary"} className="shrink-0">
          {p.tipo === "odv" ? "OdV" : "OdA"}
        </Badge>
        <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: SECTION_COLORS[m.sezione].base }} aria-hidden="true" />
        <span className="truncate">
          {m.etichetta} del {dataIt(p.data)}
        </span>
      </Link>
      <div className="min-w-0 text-xs text-ink/60 sm:flex-1 sm:text-sm">
        <span className="break-words">{p.cliente?.nome ?? "Cliente non indicato"}</span>
        {sede && p.sede && (
          <>
            {" · "}
            <Link to={`/sedi/${p.sede.id}`} className="text-primary hover:underline">
              {sede}
            </Link>
          </>
        )}
        {p.matricola && <span className="text-ink/40"> · {p.matricola}</span>}
      </div>
    </li>
  );
}

/** Il gruppo «Bozze da confermare» (PR C2): le bozze delle pratiche aperte, dalla più vecchia, col link alla pratica. */
function BozzeDaConfermare() {
  const [bozze, setBozze] = useState<statoLavoriApi.BozzaInElenco[] | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tutte, setTutte] = useState(false);

  const carica = useCallback(() => {
    setIsLoading(true);
    setErrore(null);
    statoLavoriApi
      .getBozze()
      .then(setBozze)
      .catch((err) => setErrore(getErrorMessage(err, "Impossibile caricare le bozze da confermare.")))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(carica, [carica]);

  if (isLoading && !bozze) return <div className="mt-3 h-16 animate-pulse rounded-card border border-line bg-white/60" />;
  if (errore) {
    return (
      <div className="mt-3 flex flex-wrap items-center gap-3 rounded-card border border-line bg-white p-4 text-sm text-ink/70 shadow-card" role="alert">
        <span className="min-w-0 flex-1">{errore}</span>
        <Button variant="secondary" size="sm" onClick={carica}>
          Riprova
        </Button>
      </div>
    );
  }
  if (!bozze) return null;
  if (bozze.length === 0) {
    return (
      <div className="mt-3 flex items-center gap-2 rounded-card border border-line bg-white p-4 text-sm text-ink/60 shadow-card">
        <CheckCircle2 className="h-5 w-5 shrink-0 text-[#16a34a]" aria-hidden="true" />
        Nessuna bozza da confermare nelle pratiche aperte.
      </div>
    );
  }
  const visibili = tutte ? bozze : bozze.slice(0, VOCI_INIZIALI);
  return (
    <div className="mt-3 rounded-card border border-line border-l-4 border-l-[#eab308] bg-white p-4 shadow-card">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <FilePenLine className="h-5 w-5 text-[#a16207]" aria-hidden="true" />
        <span className="text-2xl font-bold text-[#a16207]">{bozze.length}</span>
        <span className="text-sm text-ink/60">{bozze.length === 1 ? "bozza da confermare" : "bozze da confermare"}</span>
        <span className="basis-full text-xs text-ink/50 sm:basis-auto">
          <span className="hidden sm:inline">· </span>dalla più vecchia
        </span>
      </div>
      <ul className="divide-y divide-line">
        {visibili.map((b) => (
          <VoceBozza key={b.id} b={b} />
        ))}
      </ul>
      {bozze.length > VOCI_INIZIALI && (
        <button type="button" className="mt-2 text-xs font-semibold text-primary hover:underline" onClick={() => setTutte(!tutte)}>
          {tutte ? "Mostra meno" : `Mostra tutte (${bozze.length})`}
        </button>
      )}
    </div>
  );
}

function VoceBozza({ b }: { b: statoLavoriApi.BozzaInElenco }) {
  // Una bozza di installazione è della cassaforte: si apre dalla pratica più vecchia, e si dicono gli altri clienti.
  const [prima, ...altre] = b.pratiche;
  return (
    <li className="flex flex-col gap-1 py-2 sm:flex-row sm:items-center sm:gap-3">
      {prima ? (
        <Link to={`/stato-lavori/${prima.id}`} className="text-sm font-semibold text-ink hover:underline sm:w-72 sm:shrink-0">
          {TITOLO_BOZZA[b.tipo]}
          <span className="font-normal text-ink/50"> · dal {istanteIt(b.dal)}</span>
        </Link>
      ) : (
        <span className="text-sm font-semibold text-ink sm:w-72 sm:shrink-0">{TITOLO_BOZZA[b.tipo]}</span>
      )}
      <div className="min-w-0 text-xs text-ink/60 sm:flex-1 sm:text-sm">
        <span className="break-words">
          {prima ? `${prima.cliente.nome} — ${prima.sede.nome ?? "Sede senza nome"}` : "Pratica non leggibile"}
          {altre.length > 0 && ` (cassaforte condivisa con ${altre.map((x) => x.cliente.nome).join(", ")})`}
        </span>
        <span className="text-ink/40"> · {nomeCassaforte(b.cassaforte)}</span>
      </div>
    </li>
  );
}

type GruppoPassi = "miei" | "senza_responsabile" | "operativi";

const GRUPPI_PASSI: { chiave: GruppoPassi; titolo: string; vuoto: string }[] = [
  { chiave: "miei", titolo: "Miei", vuoto: "Nessun passo assegnato a te." },
  { chiave: "senza_responsabile", titolo: "Senza responsabile", vuoto: "Nessun passo senza responsabile." },
  { chiave: "operativi", titolo: "Sopralluogo, installazione e attivazione", vuoto: "Nessun sopralluogo, installazione o attivazione da fare." },
];

/**
 * Il gruppo «Passi da fare» (PR D): i passi da fare o da verificare delle
 * pratiche aperte. «Miei» e «Senza responsabile» per i passi col responsabile
 * scelto a mano (1–5, 8, A); sopralluogo, installazione e attivazione a parte,
 * chiunque li abbia in carico. Ogni voce porta al passo nella scheda.
 */
function PassiDaFare() {
  const [dati, setDati] = useState<statoLavoriApi.PassiDaFare | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const carica = useCallback(() => {
    setIsLoading(true);
    setErrore(null);
    statoLavoriApi
      .getPassiDaFare()
      .then(setDati)
      .catch((err) => setErrore(getErrorMessage(err, "Impossibile caricare i passi da fare.")))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(carica, [carica]);

  if (isLoading && !dati) return <div className="mt-3 h-16 animate-pulse rounded-card border border-line bg-white/60" />;
  if (errore) {
    return (
      <div className="mt-3 flex flex-wrap items-center gap-3 rounded-card border border-line bg-white p-4 text-sm text-ink/70 shadow-card" role="alert">
        <span className="min-w-0 flex-1">{errore}</span>
        <Button variant="secondary" size="sm" onClick={carica}>
          Riprova
        </Button>
      </div>
    );
  }
  if (!dati) return null;
  const totale = dati.miei.length + dati.senza_responsabile.length + dati.operativi.length;
  if (totale === 0) {
    return (
      <div className="mt-3 flex items-center gap-2 rounded-card border border-line bg-white p-4 text-sm text-ink/60 shadow-card">
        <CheckCircle2 className="h-5 w-5 shrink-0 text-[#16a34a]" aria-hidden="true" />
        Nessun passo da fare o da verificare nelle pratiche aperte.
      </div>
    );
  }
  return (
    <div className="mt-3 rounded-card border border-line border-l-4 border-l-primary bg-white p-4 shadow-card">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <ListChecks className="h-5 w-5 text-primary" aria-hidden="true" />
        <span className="text-2xl font-bold text-primary">{totale}</span>
        <span className="text-sm text-ink/60">{totale === 1 ? "passo da fare o da verificare" : "passi da fare o da verificare"}</span>
        <span className="basis-full text-xs text-ink/50 sm:basis-auto">
          <span className="hidden sm:inline">· </span>dalla pratica avviata prima
        </span>
      </div>
      <div className="space-y-4">
        {GRUPPI_PASSI.map((g) => (
          <SezionePassi key={g.chiave} titolo={g.titolo} vuoto={g.vuoto} voci={dati[g.chiave]} etichette={dati.etichette_stato} conResponsabile={g.chiave === "operativi"} />
        ))}
      </div>
    </div>
  );
}

function SezionePassi({
  titolo,
  vuoto,
  voci,
  etichette,
  conResponsabile,
}: {
  titolo: string;
  vuoto: string;
  voci: statoLavoriApi.VoceDaFare[];
  etichette: statoLavoriApi.PassiDaFare["etichette_stato"];
  /** Sopralluogo, installazione e attivazione: si dice chi li ha in carico. */
  conResponsabile: boolean;
}) {
  const [tutte, setTutte] = useState(false);
  const visibili = tutte ? voci : voci.slice(0, VOCI_INIZIALI);
  return (
    <section>
      <h3 className="border-b border-line pb-1.5 text-xs font-semibold text-ink/60">
        {titolo} <span className="font-normal text-ink/40">({voci.length})</span>
      </h3>
      {voci.length === 0 ? (
        <p className="pt-2 text-xs text-ink/50">{vuoto}</p>
      ) : (
        <ul className="divide-y divide-line">
          {visibili.map((v) => (
            <VocePasso key={`${v.pratica_id}:${v.passo?.tipo ?? "servizio"}`} v={v} etichette={etichette} conResponsabile={conResponsabile} />
          ))}
        </ul>
      )}
      {voci.length > VOCI_INIZIALI && (
        <button type="button" className="mt-2 text-xs font-semibold text-primary hover:underline" onClick={() => setTutte(!tutte)}>
          {tutte ? "Mostra meno" : `Mostra tutti (${voci.length})`}
        </button>
      )}
    </section>
  );
}

function VocePasso({
  v,
  etichette,
  conResponsabile,
}: {
  v: statoLavoriApi.VoceDaFare;
  etichette: statoLavoriApi.PassiDaFare["etichette_stato"];
  conResponsabile: boolean;
}) {
  const p = v.passo;
  // Sotto il titolo: cosa verificare, altrimenti cosa aspetta o cosa fare.
  const dettaglio =
    v.da_verificare.length > 0 ? v.da_verificare.join(" · ") : p?.in_attesa_di ? `In attesa di: ${p.in_attesa_di}` : p?.prossima_azione ?? null;
  return (
    <li className="flex flex-col gap-1 py-2 sm:flex-row sm:items-start sm:gap-3">
      <div className="min-w-0 sm:w-72 sm:shrink-0">
        <Link to={linkPassoDaFare(v)} className="text-sm font-semibold text-ink hover:underline">
          {p ? `${p.numero}. ${p.titolo}` : "Servizio della pratica"}
        </Link>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {p && (
            <Badge color={TONO_STATO[p.stato]}>{p.stato === "da_fare" && p.in_attesa_di ? "In attesa" : etichette[p.stato]}</Badge>
          )}
          {v.da_verificare.length > 0 && <Badge color="warning">Da verificare</Badge>}
        </div>
      </div>
      <div className="min-w-0 text-xs text-ink/60 sm:flex-1 sm:text-sm">
        <span className="break-words">
          {v.cliente.nome} — {v.sede.nome ?? "Sede senza nome"}
          {v.altre_pratiche.length > 0 && ` (cassaforte condivisa con ${v.altre_pratiche.map((x) => x.cliente.nome).join(", ")})`}
        </span>
        <span className="text-ink/40"> · {nomeCassaforte(v.cassaforte)}</span>
        {conResponsabile && v.responsabile && <span className="text-ink/50"> · {v.responsabile.nome}</span>}
        {dettaglio && (
          <p className={clsx("mt-0.5 break-words text-xs", v.da_verificare.length > 0 ? "text-amber-900" : "text-ink/50")}>{dettaglio}</p>
        )}
      </div>
    </li>
  );
}
